/* eslint-disable max-classes-per-file */
import { Injectable } from "@angular/core";
import { clone } from "lodash";
import isMatch from "lodash/isMatch";
import uniq from "lodash/uniq";
import { BasicCommand } from "./BasicCommand";
import { CommandSendType, CommandType } from "./CommandType";
import { DependencyContext } from "./DependencyContext";
import { DependencyRule } from "./DependencyRule";
import { DependencyType } from "./DependencyType";
import { OnDeleteOption } from "./OnDeleteOption";
export interface ICommand {
    $type: string;
    $commandSendType: CommandSendType;
}

export interface IMetaCommand {
    $type: string;
    key: string;
}

export type CommandReceivedCallback = (command: ICommand) => void;

export class CommandBase implements ICommand {
    $type: string;
    $commandSendType: CommandSendType;

    constructor(typeName: string, commandSendType = CommandSendType.ServerCommand) {
        this.$type = typeName;
        this.$commandSendType = commandSendType;
    }
}

export interface ICommandManager {

    destroy(): void;
    addOrUpdate<T extends ICommand>(command: T, filter: (command: T) => boolean): void;
    hasCommand<T extends BasicCommand<T>>(command: T): boolean;
    getCommand<T extends BasicCommand<T>>(command: T): T;
    remove<T extends BasicCommand<T>>(command: T): void;
    updateCommand<T>(command: BasicCommand<T>): void;
    clear(): void;
    isDirty(): boolean;
    getAsJSon(): string;
    getStack(): ICommand[];
    onCommandReceived(callback: CommandReceivedCallback): void;
    metaCommands: IMetaCommand[];
}

@Injectable()
export class CommandManager implements ICommandManager {
    private readonly metaCommandStack: IMetaCommand[] = [];
    private readonly commandstack: ICommand[] = [];
    private readonly listeners: CommandReceivedCallback[] = [];

    updateMetaCommand<T extends IMetaCommand>(metaCommand: T) {
        const idx = this.metaCommandStack.findIndex(x => x.key === metaCommand.key && x.$type === metaCommand.$type);
        if (idx >= 0) {
            this.metaCommandStack[idx] = metaCommand;
        } else {
            this.metaCommandStack.push(metaCommand);
        }
    }

    get metaCommands() {
        return clone(this.metaCommandStack);
    }

    destroy = (): void => {
        this.clear();
        this.listeners.splice(0);
    };

    addOrUpdate = <T extends ICommand>(command: T, filter: (command: T) => boolean, callback: (existingCommand: T, newCommand: T) => void = null): void => {
        const existingCommand = this.commandstack.find(x => x.$type === command.$type && filter(x as T));

        if (!existingCommand) {
            this.commandstack.push(command);
            this.triggerCommandReceivedForCommand(command);
            return;
        }

        if (callback) {
            callback(existingCommand as T, command);
        }

        Object.assign(existingCommand, command);

        this.triggerCommandReceivedForCommand(command);
    };

    hasCommand = <T extends BasicCommand<T>>(command: T): boolean => {
        const commands = this.commandstack as BasicCommand<T>[];
        return commands.some(this.filterPrimaryKeyCommand(command));
    };

    getCommand = <T extends BasicCommand<T>>(command: T): T => {
        const commands = this.commandstack as BasicCommand<T>[];
        return commands.find(this.filterPrimaryKeyCommand(command)) as T;
    };

    isDirty = () => this.commandstack.length > 0;

    remove = <T extends BasicCommand<T>>(command: T): void => {
        const commands = this.commandstack as BasicCommand<T>[];

        const existingCommand = commands.find(this.filterPrimaryKeyCommand(command));

        const index = this.commandstack.indexOf(existingCommand);

        if (index > -1) {
            this.commandstack.splice(index, 1);
            this.triggerCommandReceivedForCommand(existingCommand);
        }
    };

    updateCommand = <T>(command: BasicCommand<T>): void => {
        const commands = this.commandstack as BasicCommand<T>[];
        const existingPrimaryKeyCommands = commands.filter(this.filterGroupPrimaryKeyCommand<T>(command));

        switch (command.commandType) {
            case CommandType.Delete: {
                // Får vi in en delete så ska den kvittas mot existerande "Insert", annars läggas till
                // Alla "Replace" ska tas bort
                const existingInsert = existingPrimaryKeyCommands.find(p => p.commandType === CommandType.Insert && p.groupKey === command.groupKey);
                const replacements = existingPrimaryKeyCommands.filter(p => p.commandType === CommandType.Replace && p.groupKey === command.groupKey);

                replacements.forEach((cmd) => {
                    const index = commands.indexOf(cmd);
                    commands.splice(index, 1);
                });

                if (existingInsert) {
                    const index = commands.indexOf(existingInsert);
                    commands.splice(index, 1);
                } else {
                    const insertIndex = this.getInsertIndex(command, commands);
                    commands.splice(insertIndex, 0, command);
                }

                this.handleCascadeDelete(command, true);
                break;
            }
            case CommandType.Insert: {
                // Kan inte finnas existerande delete före insert
                // Får vi in en delete så ska den kvittas mot existerande "Insert", annars läggas till
                // Alla "Replace" ska tas bort
                const deleteCmd = existingPrimaryKeyCommands.find(p => (p.commandType === CommandType.Delete) && p.groupKey === command.groupKey);
                if (deleteCmd) {
                    const index = commands.indexOf(deleteCmd);
                    commands.splice(index, 1);
                } else {
                    const insertIndex = this.getInsertIndex(command, commands);
                    commands.splice(insertIndex, 0, command);
                }
                break;
            }
            case CommandType.Replace:
                // Bara att ersätta befintliga replaces. Om det är en insert som ligger i kön så bevaras den commandTypen.
                this.addOrUpdate(command, this.groupSelector(command, c => c.primaryKey, c => c.primaryKey), (existingCommand, newCommand) => {
                    if (existingCommand.commandType === CommandType.Insert) {
                        // Om det ligger ett insert-command som vi försöker ersätta så bevarar vi alltid den som typ "Insert"
                        // Det gör att om det kommer en efterkommande delete så kan dom kvittas, ligger den kvar som "Replace" så kommer det bli Delete + Replace.
                        newCommand.commandType = existingCommand.commandType;
                    }
                });
                break;
        }

        this.triggerCommandReceivedForCommand(command);
    };

    clear = (): void => {
        this.commandstack.splice(0);
        this.metaCommandStack.splice(0);
    };

    getAsJSon = (): string => {
        if (!this.commandstack.length) {
            return null;
        }

        return JSON.stringify(this.commandstack.filter(c => c.$commandSendType === CommandSendType.ServerCommand));
    };

    getStack = () => this.commandstack.filter(c => c.$commandSendType === CommandSendType.ServerCommand);

    onCommandReceived(fn: CommandReceivedCallback) {
        this.listeners.push(fn);
    }

    private filterPrimaryKeyCommand<T>(command: BasicCommand<T>): (value: BasicCommand<T>, index: number, array: BasicCommand<T>[]) => unknown {
        return p => p.$type === command.$type && (isMatch(command.primaryKey, p.primaryKey) || isMatch(p.primaryKey, command.primaryKey));
    }

    private filterGroupPrimaryKeyCommand<T>(command: BasicCommand<T>): (value: BasicCommand<T>, index: number, array: BasicCommand<T>[]) => unknown {
        return p => p.groupKey === command.groupKey && (isMatch(command.primaryKey, p.primaryKey) || isMatch(p.primaryKey, command.primaryKey));
    }

    private handleCascadeDelete(command: BasicCommand<any>, isRootCommand: boolean) {
        const children = this.traverse(command,
            dependency => dependency.type === DependencyType.Child &&
                dependency.principal === command.groupKey &&
                dependency.onDeleteOption === OnDeleteOption.cascade,
            (p, rule) => p.principal === rule.dependent && p.onDeleteOption === OnDeleteOption.cascade);

        const commands = this.commandstack as BasicCommand<any>[];
        const childCommands = this.getMatchingCommands(commands, children, command);
        childCommands.forEach(p => {
            this.handleCascadeDelete(p, false);
        });

        if (!isRootCommand) {
            const index = commands.indexOf(command);
            commands.splice(index, 1);
        }
    }

    private traverse(rootCommand: BasicCommand<any>,
        rootFilter: (dependency: DependencyRule) => boolean,
        nodeFilter: (dependency: DependencyRule, rule: DependencyRule) => boolean) {
        const dependencies = DependencyContext.dependencies(rootCommand.groupKey);
        const children = dependencies.filter(rootFilter);
        const visitedPrincipals = [];
        const principalTraversal = (rule: DependencyRule) => {
            const principals = uniq(dependencies.filter(node => nodeFilter(node, rule)));

            principals.filter(g => visitedPrincipals.indexOf(g) === -1)
                .forEach(dep => {
                    visitedPrincipals.push(dep);
                    principalTraversal(dep);
                    children.push(dep);
                });
        };

        children.forEach(principalTraversal);
        return children;
    }

    private groupSelector<T>(command: BasicCommand<T>, commandKeySelector: (arg: BasicCommand<any>) => any, existingCommandKeySelector: (arg: BasicCommand<any>) => any) {
        return (p) => (isMatch(commandKeySelector(command), existingCommandKeySelector(p)) || isMatch(existingCommandKeySelector(p), commandKeySelector(command)));
    }

    private triggerCommandReceivedForCommand(command: ICommand) {
        this.listeners.forEach(x => {
            x(command);
        });
    }

    private findChild<T>(
        existingCommands: BasicCommand<T>[],
        commandDependencies: DependencyRule[],
        comparator: (...arr: number[]) => number,
        command: BasicCommand<T>) {
        let dependentIndex;
        commandDependencies.forEach(rule => {
            const foundCommands = this.getCommands<T>(rule, command, existingCommands);
            if (foundCommands.length > 0) {
                const value = comparator(...foundCommands.map(g => existingCommands.indexOf(g)));
                if (dependentIndex === undefined) {
                    dependentIndex = value;
                } else {
                    dependentIndex = comparator(value, dependentIndex);
                }
            }
        });

        if (dependentIndex === undefined) {
            return -1;
        }

        return dependentIndex;
    }

    private getMatchingCommands<T>(
        existingCommands: BasicCommand<T>[],
        commandDependencies: DependencyRule[],
        command: BasicCommand<T>) {
        const children: BasicCommand<T>[] = [];
        commandDependencies.forEach(rule => {
            const foundChildren = this.getCommands<T>(rule, command, existingCommands);
            children.push(...foundChildren);
        });

        return children;
    }

    private getCommands<T>(rule: DependencyRule, command: BasicCommand<T>, existingCommands: BasicCommand<T>[]) {
        let foundChildren;
        if (rule.dependent === command.groupKey) {
            foundChildren = existingCommands.filter(p => p.groupKey === rule.principal &&
                this.groupSelector(command, rule.dependentKey, rule.principalKey)(p));
        } else {
            foundChildren = existingCommands.filter(p => p.groupKey === rule.dependent &&
                this.groupSelector(command, rule.principalKey, rule.dependentKey)(p));
        }
        return foundChildren;
    }

    private findFirstDependent<T>(children: DependencyRule[], commands: BasicCommand<T>[], command: BasicCommand<T>) {
        return this.findChild(commands, children, Math.min, command);
    }

    private findLastDependent<T>(children: DependencyRule[], commands: BasicCommand<T>[], command: BasicCommand<T>) {
        return this.findChild(commands, children, Math.max, command);
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    private getInsertIndex<T>(command: BasicCommand<T>, commands: BasicCommand<T>[]) {
        const dependencies = DependencyContext.dependencies(command.groupKey);

        const children = this.traverse(command, p => p.type === DependencyType.Child && p.principal === command.groupKey, (p, rule) => p.principal === rule.dependent);
        const parents = this.traverse(command, p => p.type === DependencyType.Child && p.dependent === command.groupKey, (p, rule) => p.dependent === rule.principal);

        let minIndex = 0;
        let maxIndex = commands.length;
        if (children.length > 0) {
            if (command.commandType === CommandType.Insert) {
                // Vi har barn till något annat command och ska inserta - vi behöver därför ligga före våra barn
                const childIndex = this.findFirstDependent(children, commands, command);
                if (childIndex >= 0) {
                    maxIndex = childIndex;
                }
            } else if (command.commandType === CommandType.Delete) {
                // Vi har barn till något annat command och ska deletea - vi behöver därför ligga efter våra barn
                const childIndex = this.findLastDependent(children, commands, command);
                if (childIndex >= 0) {
                    minIndex = childIndex + 1;
                }
            }
        }

        if (parents.length > 0) {
            if (command.commandType === CommandType.Insert) {
                // Vi är barn till något annat command och ska inserta - vi behöver därför ligga efter vår förälder
                const childIndex = this.findLastDependent(parents, commands, command);
                if (childIndex >= 0) {
                    minIndex = Math.max(minIndex, childIndex + 1);
                }
            } else if (command.commandType === CommandType.Delete) {
                // Vi är barn till något annat command och ska deletea - vi behöver därför ligga före vår förälder
                const childIndex = this.findFirstDependent(parents, commands, command);
                if (childIndex >= 0) {
                    maxIndex = Math.min(maxIndex, childIndex);
                }
            }
        }

        const replaceCommand = commands.find(p => p.groupKey === command.groupKey
            && this.groupSelector(command, c => c.primaryKey, c => c.primaryKey)(p) && p.commandType === CommandType.Replace);
        minIndex = Math.max(commands.indexOf(replaceCommand), minIndex);
        minIndex = Math.max(minIndex, this.forcedLastIndex(dependencies, command, commands));
        if (minIndex <= maxIndex) {
            return minIndex;
        } else {
            return maxIndex;
        }
    }

    private forcedLastIndex<T>(
        dependencies: DependencyRule[],
        command: BasicCommand<T>,
        existingCommands: BasicCommand<T>[]): number {

        const principalsFirst = dependencies.filter(p => p.type === DependencyType.PrincipalAlwaysLast);
        const alwaysInsertAfterGroups = [];
        const alwaysInsertBeforeGroups = [];

        const dependentTraversal = (group: string) => {
            const dependents = uniq(dependencies.filter(p => p.principal === group).map(g => g.dependent));
            dependents.forEach(dependentTraversal);
            alwaysInsertAfterGroups.push(...dependents);
        };

        principalsFirst.forEach(rule => {
            if (rule.principal !== command.groupKey) {
                // Always insert after principal and dependents
                alwaysInsertAfterGroups.push(rule.principal);
                dependentTraversal(rule.dependent);
            } else {
                // Always insert before  principal and dependents
                alwaysInsertBeforeGroups.push(rule.principal);
                dependentTraversal(rule.dependent);
            }
        });

        let minIndex;
        let maxIndex;

        alwaysInsertAfterGroups.forEach(dependency => {
            minIndex = Math
                .max(...existingCommands.filter(p => p.groupKey === dependency
                    && (isMatch(p.primaryKey, command.primaryKey) || isMatch(command.primaryKey, p.primaryKey)))
                    .map((g) => existingCommands.indexOf(g)), minIndex);
        });

        alwaysInsertBeforeGroups.forEach(dependency => {
            maxIndex = Math
                .min(...existingCommands.filter(p => p.groupKey === dependency
                    && (isMatch(p.primaryKey, command.primaryKey) || isMatch(command.primaryKey, p.primaryKey)))
                    .map((g) => existingCommands.indexOf(g)), maxIndex);
        });

        if (minIndex > 0) {
            return minIndex;
        } else if (maxIndex > 0) {
            return maxIndex;
        } else {
            return -1;
        }
    }
}
